import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { AuthUser, LoginInput, LoginResponse, PermissionKey } from '@bsl/shared';
import { api, bindSession, refreshSession } from '@/lib/http';

export const useAuthStore = defineStore('auth', () => {
  // The access token lives only in memory; the refresh token is an httpOnly cookie the page cannot read.
  const accessToken = ref<string | null>(null);
  const user = ref<AuthUser | null>(null);
  const initialized = ref(false);
  const sessionExpired = ref(false);

  const isAuthenticated = computed(() => !!accessToken.value && !!user.value);
  const permissions = computed(() => new Set(user.value?.permissions ?? []));

  function can(...keys: PermissionKey[]) {
    return keys.every((k) => permissions.value.has(k));
  }
  function canAny(...keys: PermissionKey[]) {
    return keys.some((k) => permissions.value.has(k));
  }

  function setSession(res: { accessToken: string; user: AuthUser }) {
    accessToken.value = res.accessToken;
    user.value = res.user;
    sessionExpired.value = false;
  }

  function clear() {
    accessToken.value = null;
    user.value = null;
  }

  bindSession({
    getToken: () => accessToken.value,
    onRefreshed: (res) => setSession(res),
    onSessionLost: () => {
      if (user.value) sessionExpired.value = true;
      clear();
    },
  });

  /** On page load, restore the session from the refresh cookie if there is one. */
  async function init() {
    if (initialized.value) return;
    try {
      await refreshSession();
    } catch {
      clear();
    } finally {
      initialized.value = true;
    }
  }

  async function login(input: LoginInput) {
    const res = await api.post<LoginResponse>('/auth/login', input);
    setSession(res);
    return res.user;
  }

  async function logout() {
    try {
      await api.post('/auth/logout');
    } finally {
      clear();
    }
  }

  async function reloadUser() {
    user.value = await api.get<AuthUser>('/auth/me');
  }

  return {
    accessToken,
    user,
    initialized,
    sessionExpired,
    isAuthenticated,
    permissions,
    can,
    canAny,
    setSession,
    init,
    login,
    logout,
    reloadUser,
  };
});
